import {isColorLike} from "@cm/math"
import {Batching, Descriptor} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {EvaluatedParameters, GetParameters} from "@cm/graph-v2-final"
import {ImageOpDefinition, isAnyImageOpResult} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {SourceType} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/image-op-per-pixel-fn/definition"
import {HalCompositorInfo} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/image-op-per-pixel-fn/hal"

export const determineDescriptor = <Definition extends ImageOpDefinition>(
    parameters: EvaluatedParameters<GetParameters<Definition>>,
    compositingInfo: HalCompositorInfo,
): Descriptor => {
    const sources = compositingInfo.sourceParameterNames.map((sourceParameterName) => parameters[sourceParameterName] as SourceType)
    return computeDescriptorFromSources(sources, compositingInfo.outDescriptor)
}

const computeDescriptorFromSources = (sources: readonly SourceType[], outDescriptorOverride?: Partial<Descriptor>): Descriptor => {
    // let width: number | undefined
    // let height: number | undefined
    let resultChannels: number | undefined
    let resultPatchSizeX: number | undefined
    let resultPatchSizeY: number | undefined
    let resultNumPatchesX: number | undefined
    let resultNumPatchesY: number | undefined
    const setOrExpand = (currValue: number | undefined, sourceValue: number) => {
        if (sourceValue < 1) {
            throw new Error("ImageOpFnPixelTransform: All sources must have at least one channel")
        }
        if (currValue === undefined) {
            return sourceValue
        }
        if (sourceValue === 1) {
            return currValue
        }
        if (currValue === 1) {
            return sourceValue
        }
        if (currValue !== sourceValue) {
            throw new Error("ImageOpFnPixelTransform: All sources must have the same number of channels or a single channel for broadcasting")
        }
        return currValue
    }
    sources.forEach((source) => {
        if (isAnyImageOpResult(source)) {
            let patchSizeX, patchSizeY, numPatchesX, numPatchesY
            if (source.descriptor.batching) {
                patchSizeX = source.descriptor.batching.patchSize.width
                patchSizeY = source.descriptor.batching.patchSize.height
                numPatchesX = source.descriptor.batching.numPatches.x
                numPatchesY = source.descriptor.batching.numPatches.y
            } else {
                patchSizeX = source.descriptor.width
                patchSizeY = source.descriptor.height
                numPatchesX = 1
                numPatchesY = 1
            }
            resultPatchSizeX = setOrExpand(resultPatchSizeX, patchSizeX)
            resultPatchSizeY = setOrExpand(resultPatchSizeY, patchSizeY)
            resultNumPatchesX = setOrExpand(resultNumPatchesX, numPatchesX)
            resultNumPatchesY = setOrExpand(resultNumPatchesY, numPatchesY)
            resultChannels = setOrExpand(resultChannels, source.descriptor.channels)
        } else if (isColorLike(source)) {
            const colorChannels = source.a !== undefined ? 4 : 3
            // width = setOrExpand(width, 1)
            // height = setOrExpand(height, 1)
            resultPatchSizeX = setOrExpand(resultPatchSizeX, 1)
            resultPatchSizeY = setOrExpand(resultPatchSizeY, 1)
            resultNumPatchesX = setOrExpand(resultNumPatchesX, 1)
            resultNumPatchesY = setOrExpand(resultNumPatchesY, 1)
            resultChannels = setOrExpand(resultChannels, colorChannels)
        } else if (Array.isArray(source)) {
            // width = setOrExpand(width, 1)
            // height = setOrExpand(height, 1)
            resultPatchSizeX = setOrExpand(resultPatchSizeX, 1)
            resultPatchSizeY = setOrExpand(resultPatchSizeY, 1)
            resultNumPatchesX = setOrExpand(resultNumPatchesX, 1)
            resultNumPatchesY = setOrExpand(resultNumPatchesY, 1)
            resultChannels = setOrExpand(resultChannels, source.length)
        } else {
            // width = setOrExpand(width, 1)
            // height = setOrExpand(height, 1)
            // channels = setOrExpand(channels, 1)
            resultPatchSizeX = setOrExpand(resultPatchSizeX, 1)
            resultPatchSizeY = setOrExpand(resultPatchSizeY, 1)
            resultNumPatchesX = setOrExpand(resultNumPatchesX, 1)
            resultNumPatchesY = setOrExpand(resultNumPatchesY, 1)
            resultChannels = setOrExpand(resultChannels, 1)
        }
    })
    resultPatchSizeX ??= 0
    resultPatchSizeY ??= 0
    resultNumPatchesX ??= 1
    resultNumPatchesY ??= 1
    let resultWidth = resultPatchSizeX * resultNumPatchesX
    let resultHeight = resultPatchSizeY * resultNumPatchesY
    if (outDescriptorOverride) {
        if (outDescriptorOverride.width) {
            resultWidth = outDescriptorOverride.width
        }
        if (outDescriptorOverride.height) {
            resultHeight = outDescriptorOverride.height
        }
        if (outDescriptorOverride.channels) {
            resultChannels = outDescriptorOverride.channels
        }
        if (outDescriptorOverride.batching) {
            if (outDescriptorOverride.batching.patchSize) {
                resultPatchSizeX = outDescriptorOverride.batching.patchSize.width
                resultPatchSizeY = outDescriptorOverride.batching.patchSize.height
            }
            if (outDescriptorOverride.batching.numPatches) {
                resultNumPatchesX = outDescriptorOverride.batching.numPatches.x
                resultNumPatchesY = outDescriptorOverride.batching.numPatches.y
            }
        }
    }
    if (!resultWidth || !resultHeight || !resultChannels) {
        throw new Error("ImageOpFnPixelTransform: Invalid result image dimensions")
    }
    const batching: Batching | undefined =
        resultNumPatchesX > 1 || resultNumPatchesY > 1
            ? {
                  numPatches: {x: resultNumPatchesX, y: resultNumPatchesY},
                  patchSize: {width: resultPatchSizeX, height: resultPatchSizeY},
              }
            : undefined
    return {
        width: resultWidth,
        height: resultHeight,
        channels: resultChannels,
        batching,
    }
}
